<template>
  <div
    v-if="form"
    class="vgs-form"
    :class="`variant--${variant}`"
  >
    <template v-if="variant === 'default'">
      <div class="flex-grid-1-col gutter-md">
        <vgs-input
          v-bind="{
            fieldClass,
            fieldName: 'card-holder',
            fieldLabel: cardHolder,
            haveBeenSubmited,
            form,
            css: isRtl ? cssRtl : css,
            options: {
              type: 'text',
              name: 'CardHolderName',
              ariaLabel: cardHolder,
              placeholder: '',
              validations: ['required'],
            },
          }"
          @fire-vgs-event="vgsEventTag"
        />
        <vgs-input
          v-bind="{
            fieldClass,
            fieldName: 'card-number',
            fieldLabel: cardNumberField,
            haveBeenSubmited,
            form,
            css: isRtl ? cssRtl : css,
            options: {
              type: 'card-number',
              name: 'CardNumber',
              ariaLabel: cardNumberField,
              successColor: '#4F8A10',
              errorColor: '#D8000C',
              placeholder: '',
              showCardIcon: {
                right: isRtl ? '' : '16px',
                left: isRtl ? '16px' : '',
              },
              validations: ['required', 'validCardNumber'],
              autoComplete: 'cc-number',
            },
          }"
          @fire-vgs-event="vgsEventTag"
        />
        <vgs-input
          v-bind="{
            fieldClass,
            fieldName: 'card-exp',
            fieldLabel: expDateField,
            haveBeenSubmited,
            form,
            css: isRtl ? cssRtl : css,
            options: {
              type: 'card-expiration-date',
              name: 'CardExpirateDate',
              ariaLabel: expDateField,
              successColor: '#4F8A10',
              errorColor: '#D8000C',
              placeholder: '',
              yearLength: '2',
              validations: ['required', 'validCardExpirationDate'],
              autoComplete: 'cc-exp',
            },
          }"
          @fire-vgs-event="vgsEventTag"
        />
      </div>
    </template>
    <template v-if="variant === 'modify'">
      <div class="flex-grid-1-col gutter-md">
        <vgs-input
          v-bind="{
            fieldClass,
            fieldName: 'card-number',
            fieldLabel: cardNumberField,
            haveBeenSubmited,
            form,
            css: {
              ...(isRtl ? cssRtl : css),
              'padding-left': isRtl ? '' : '55px',
              'padding-right': isRtl ? '55px' : '',
            },
            options: {
              type: 'card-number',
              name: 'CardNumber',
              ariaLabel: cardNumberField,
              successColor: '#4F8A10',
              errorColor: '#D8000C',
              placeholder: '',
              showCardIcon: {
                left: isRtl ? '' : '16px',
                right: isRtl ? '16px' : '',
              },
              validations: ['required', 'validCardNumber'],
              autoComplete: 'cc-number',
            },
          }"
          @fire-vgs-event="vgsEventTag"
        />
        <vgs-input
          v-bind="{
            fieldClass,
            fieldName: 'card-exp',
            fieldLabel: expDateField,
            haveBeenSubmited,
            form,
            css: isRtl ? cssRtl : css,
            options: {
              type: 'card-expiration-date',
              name: 'CardExpirateDate',
              ariaLabel: expDateField,
              successColor: '#4F8A10',
              errorColor: '#D8000C',
              placeholder: '',
              yearLength: '2',
              validations: ['required', 'validCardExpirationDate'],
              autoComplete: 'cc-exp',
            },
          }"
          @fire-vgs-event="vgsEventTag"
        />
        <vgs-input
          v-bind="{
            fieldClass,
            fieldName: 'card-holder',
            fieldLabel: cardHolder,
            haveBeenSubmited,
            form,
            css: isRtl ? cssRtl : css,
            options: {
              type: 'text',
              name: 'CardHolderName',
              ariaLabel: cardHolder,
              placeholder: '',
              validations: ['required'],
            },
          }"
          @fire-vgs-event="vgsEventTag"
        />
      </div>
    </template>
  </div>
</template>

<script>
  import { mapState, mapGetters } from 'vuex'
  import { loadVGSCollect } from '@vgs/collect-js'
  import VariantMixin from '@mixins/variant-mixin'
  import VgsInput from './components/vgs-input.vue'

  /**
   * Cards supported
   *
   * The keys are the supported VGS name
   * The values are SynXis supported codes
   * All the supported codes from SynXis are in the object, but not all of them are supported by VGS
   */
  const cardCodeMap = {
    amex: 'AX', // American Express
    visa: 'VI', // Visa
    visaelectron: 'VI', // Visa Electron
    maestro: 'SW', // Maestro
    mastercard: 'MC', // Master Card
    unionpay: 'UP', // Union Pay CU (for all other channels)
    dinersclub: 'DN', // Diners Club
    discover: 'DS', // Discover Card
    forbrugsforeningen: 'FB', // Forbrugsforeningskort
    jcb: 'JC', // Japanese Credit Bureau Credit Card
    // Not Supported by VGS
    blanche: 'CB', // Carte Blanche
    chinaUPe: 'CU', // China Union Pay CU (for Expedia only)
    euro: 'EC', // Euro card
    japCB: 'JC', // Japanese Credit Bureau Credit Card
    // Default
    other: 'OT', // Other cards (not supported)
  }

  let environment = 'default'
  const body = document.getElementsByTagName('body')[0]
  body.classList.forEach((c) => {
    if (c.includes('env:')) {
      environment = c.replace('env:', '')
    }
  })
  /**
   * Creates the main VGS form for secure credit card data capturing
   */
  export default {
    name: 'VgsForm',
    components: {
      VgsInput,
    },
    mixins: [
      VariantMixin(
        // Component name
        'vgs-form',
        // Automatic generated computed properties
        ['vaultId', 'endpoint', 'environment'],
        // Force variant type
        environment
      ),
      VariantMixin(
        // Component name
        'vgs-form',
        // Automatic generated computed properties
        ['wrapperClass', 'css', 'cssRtl', 'fieldClass']
      ),
    ],
    props: {
      /**
       * Card holder label
       */
      cardHolder: {
        type: String,
        default: 'Cardholder Name',
      },
      /**
       * Card number label
       */
      cardNumberField: {
        type: String,
        default: 'Card number',
      },
      /**
       * Expiration date label
       */
      expDateField: {
        type: String,
        default: 'Expiry date',
      },
      /**
       * Cvc field label
       */
      cvcField: {
        type: String,
        default: 'CVC',
      },
      /**
       * Vgs variant - "default" for the regular Booking and the "modify" for the Modify Booking
       */
      variant: {
        type: String,
        required: true,
        validator(value) {
          return ['default', 'modify'].includes(value)
        },
      },
      vgsEndpoint: {
        type: String,
        required: true,
      },
    },
    data: () => ({
      /**
       * @vuese
       * Stores the VGS collect instance
       */
      collect: null,
      /**
       * @vuese
       * Stores the VGS form instance
       */
      form: null,
      /**
       * @vuese
       * Tracks if the form have been submitted
       */
      haveBeenSubmited: false,
      fields: ['card-holder', 'card-number', 'card-exp', 'card-cvc'],
      /**
       * @vuese
       * Tracks input focus or blur for tracking
       */
      eventFired: false,
    }),
    computed: {
      ...mapState('global', ['isSitecore']),
      ...mapGetters('global', ['isRtl']),
      ...mapGetters('booking', ['getQuery', 'getBookingTrackingV2']),
      name() {
        return 'vgs-form'
      },
    },
    async mounted() {
      try {
        this.collect = await loadVGSCollect({
          vaultId: this.vaultId,
          environment: this.environment,
          version: '2.15.0',
        })
        this.form = this.collect.init()
        this.form.useCname(this.vgsEndpoint)
      } catch (err) {
        this.$logger.error(err)
      }
    },
    methods: {
      /**
       * @vuese
       * Submits form data for VGS and return the same form data with the additional credit card fields
       * @arg Check-out form data
       */
      async submit(data) {
        const endpoint = data.isBookingEditMode ? '/api/v1/booking/ModifyReservation' : '/api/v1/booking/check-out'

        data.CardCode = cardCodeMap[this.form.state.CardNumber.cardType] ?? cardCodeMap.other
        return new Promise((resolve, reject) => {
          this.haveBeenSubmited = false
          this.form.submit(
            endpoint,
            {
              data,
              withCredentials: true
            },
            (status, response) => {
              this.haveBeenSubmited = true
              if (status === 200) {
                response.isDataFromVGS = true
                return resolve(response)
              }
              throw reject(response)
            },
            (error) => {
              this.$logger.error(error)
              this.haveBeenSubmited = true
              this.$el.scrollIntoView()
              throw reject(error)
            }
          )
        })
      },
      vgsEventTag() {
        if (!this.eventFired) {
          this.eventFired = true
          this.$track(
            this.getBookingTrackingV2(this.getQuery, { event: 'step7' })
          )
        }
      },
    },
  }
</script>
